import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import PreviewIcon from "@mui/icons-material/Preview";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import axios from "axios";
import TitleHeader from "../../components/TitleHeader";
import Loading from "../../components/Loading";
import useToast from "../../components/useToast";
import { API_BASE_URL } from "../../config";
import { useTranslation } from "react-i18next";

const COMPANY_PROFILE = {
  logoPath: "/assets/TimeTab_Logo_quer_cmyk_pos.png",
  qrCodePath: "/assets/payment-qr-code.png",
  senderName: "InnoTab AG",
  senderAddressLines: ["Luegetalmatte 5", "6133 Hergiswil"],
  senderContactLines: ["info@timetab.ch", "www.timetab.ch"],
  senderVatLabel: "CHE-164.890.426",
  paymentNote: "Wir bitten Sie um die Überweisung des Rechnungsbetrages innerhalb von 30 Tagen.",
  closingLines: ["Freundliche Grusse", "InnoTab AG"],
};

const emptyForm = {
  invoice_customer_id: "",
  invoice_number: "",
  business_unit: "",
  customer_company_name: "",
  customer_email: "",
  invoice_value_net: "",
  invoice_tax: "",
  creation_date: "",
  subscription_months: "1",
  reminder_date: "",
  invoice_sent: false,
  invoice_paid: false,
};

const emptyCustomerForm = {
  company_name: "",
  care_of: "",
  street: "",
  first_name: "",
  last_name: "",
  email: "",
  zip: "",
  city: "",
  country: "",
  subscription_name: "",
  unit_cost: "",
};

const createEmptyInvoiceItem = () => ({
  description: "",
  quantity: "1",
  unit_price: "0.00",
});

const buildCustomerCityLine = (customer = null) => {
  const safeCustomer = customer || {};
  return [safeCustomer.zip, safeCustomer.city].filter(Boolean).join(" ").trim();
};

const buildCustomerCountryLine = (customer = null) => {
  const safeCustomer = customer || {};
  return [buildCustomerCityLine(safeCustomer), safeCustomer.country].filter(Boolean).join(", ").trim();
};

const createInvoiceDraft = (row = {}, customer = null) => {
  const addressLine1 = customer?.care_of ? `c/o ${customer.care_of}` : customer?.street || "";
  const addressLine2 = customer?.care_of ? customer?.street || "" : buildCustomerCityLine(customer);
  const addressLine3 = customer?.care_of
    ? buildCustomerCountryLine(customer)
    : customer?.country || "";

  return {
    sourceInvoiceId: row.id || null,
    invoiceCustomerId: customer?.id ? String(customer.id) : "",
    invoiceNumber: row.invoice_number || "",
    invoiceDate: new Date().toISOString().slice(0, 10),
    customerCompanyName: customer?.company_name || row.customer_company_name || "",
    customerEmail: customer?.email || row.customer_email || "",
    customerAddressLine1: addressLine1,
    customerAddressLine2: addressLine2,
    customerAddressLine3: addressLine3,
    timeframeStart: row.creation_date || "",
    timeframeEnd: row.expected_next_invoice_date || "",
    title: "Rechnung",
    introLabel: "Abo-Zeitraum",
    note: COMPANY_PROFILE.paymentNote,
    closingLine1: COMPANY_PROFILE.closingLines[0],
    closingLine2: COMPANY_PROFILE.closingLines[1],
    lineItems: [
      {
        description: row.business_unit
          ? `${row.business_unit}`
          : customer?.subscription_name
            ? `${customer.subscription_name}`
            : "TimeTab Subscription",
        quantity: "1",
        unit_price: String(
          Number(
            row.invoice_value_net ?? customer?.unit_cost ?? 0
          ).toFixed(2)
        ),
      },
    ],
  };
};

const addMonths = (dateString, monthsRaw) => {
  if (!dateString || !monthsRaw) return "";
  const months = Number(monthsRaw);
  if (!Number.isInteger(months) || months <= 0) return "";

  const [year, month, day] = dateString.split("-").map(Number);
  if (!year || !month || !day) return "";

  const candidate = new Date(year, month - 1 + months, day);
  if (Number.isNaN(candidate.getTime())) return "";
  return candidate.toISOString().slice(0, 10);
};

const formatAmount = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "0.00";
  return amount.toFixed(2);
};

const formatCurrency = (value) => `CHF ${formatAmount(value)}`;

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("de-CH").format(date);
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const assetDataUrlCache = new Map();

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const loadAssetAsDataUrl = async (assetPath) => {
  if (!assetDataUrlCache.has(assetPath)) {
    assetDataUrlCache.set(
      assetPath,
      fetch(assetPath)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to load asset: ${assetPath}`);
          }
          return response.blob();
        })
        .then(blobToDataUrl)
    );
  }

  return assetDataUrlCache.get(assetPath);
};

const toInvoiceDocumentModel = (draft) => {
  const items = (draft.lineItems || [])
    .map((item) => {
      const quantity = Number(item.quantity || 0);
      const unitPrice = Number(item.unit_price || 0);
      return {
        description: (item.description || "").trim(),
        quantity: Number.isFinite(quantity) ? quantity : 0,
        unitPrice: Number.isFinite(unitPrice) ? unitPrice : 0,
        total: (Number.isFinite(quantity) ? quantity : 0) * (Number.isFinite(unitPrice) ? unitPrice : 0),
      };
    })
    .filter((item) => item.description);

  const total = items.reduce((sum, item) => sum + item.total, 0);

  return {
    invoiceNumber: (draft.invoiceNumber || "").trim(),
    invoiceDate: draft.invoiceDate || "",
    title: (draft.title || "Rechnung").trim(),
    introLabel: (draft.introLabel || "Abo-Zeitraum").trim(),
    timeframeStart: draft.timeframeStart || "",
    timeframeEnd: draft.timeframeEnd || "",
    customerLines: [
      draft.customerCompanyName,
      draft.customerAddressLine1,
      draft.customerAddressLine2,
      draft.customerAddressLine3,
      draft.customerEmail ? `E-Mail: ${draft.customerEmail}` : "",
    ].filter(Boolean),
    note: (draft.note || "").trim(),
    closingLines: [draft.closingLine1, draft.closingLine2].filter(Boolean),
    items,
    total,
  };
};

const buildInvoiceHtml = async (draft) => {
  const invoice = toInvoiceDocumentModel(draft);
  const [logoSrc, qrCodeSrc] = await Promise.all([
    loadAssetAsDataUrl(COMPANY_PROFILE.logoPath),
    loadAssetAsDataUrl(COMPANY_PROFILE.qrCodePath),
  ]);
  const timeframeLabel =
    invoice.timeframeStart && invoice.timeframeEnd
      ? `${invoice.introLabel}: ${formatDate(invoice.timeframeStart)} - ${formatDate(invoice.timeframeEnd)}`
      : invoice.introLabel;

  const itemRows = invoice.items
    .map(
      (item) => `
        <tr>
          <td>${escapeHtml(item.description)}</td>
          <td>${escapeHtml(item.quantity)}</td>
          <td>${escapeHtml(formatCurrency(item.unitPrice))}</td>
          <td>${escapeHtml(formatCurrency(item.total))}</td>
        </tr>
      `
    )
    .join("");

  const customerLines = invoice.customerLines
    .map((line) => `<div>${escapeHtml(line)}</div>`)
    .join("");

  const senderLines = [
    COMPANY_PROFILE.senderName,
    ...COMPANY_PROFILE.senderAddressLines,
    "",
    ...COMPANY_PROFILE.senderContactLines,
    "",
    COMPANY_PROFILE.senderVatLabel,
  ]
    .map((line) => (line ? `<div>${escapeHtml(line)}</div>` : `<div>&nbsp;</div>`))
    .join("");

  const paymentLines = [
    "Konto/Zahlbar an",
    "CH82 0028 8288 1492 4101 U",
    COMPANY_PROFILE.senderName,
    ...COMPANY_PROFILE.senderAddressLines,
  ]
    .map((line) => `<div>${escapeHtml(line)}</div>`)
    .join("");

  const payableByLines = invoice.customerLines
    .slice(0, 4)
    .map((line) => `<div>${escapeHtml(line)}</div>`)
    .join("");

  return `<!DOCTYPE html>
  <html lang="de">
    <head>
      <meta charset="utf-8" />
      <title>${escapeHtml(invoice.invoiceNumber || "Invoice")}</title>
      <style>
        @page {
          size: A4;
          margin: 0;
        }
        * {
          box-sizing: border-box;
        }
        body {
          margin: 0;
          font-family: Arial, Helvetica, sans-serif;
          color: #111;
          background: #f3f4f6;
        }
        .page {
          width: 210mm;
          min-height: 292mm;
          margin: 0 auto;
          background: #fff;
          padding: 8mm 8mm 0;
          display: flex;
          flex-direction: column;
        }
        .logo {
          width: 64mm;
          height: auto;
          display: block;
          margin-bottom: 8mm;
        }
        .top {
          display: flex;
          justify-content: space-between;
          gap: 12mm;
          align-items: flex-start;
        }
        .sender,
        .customer {
          font-size: 11px;
          line-height: 1.35;
        }
        .customer {
          margin-top: 20mm;
          min-width: 54mm;
        }
        .date-line {
          text-align: right;
          margin-top: 10mm;
          font-size: 11px;
        }
        h1 {
          margin: 9mm 0 5mm;
          font-size: 17px;
        }
        .timeframe {
          font-size: 11px;
          margin-bottom: 5mm;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
        }
        th, td {
          border: 2px solid #fff;
          background: #d9d9d9;
          padding: 8px 12px;
          text-align: left;
        }
        th {
          font-weight: 700;
        }
        .total-row td {
          font-weight: 700;
        }
        .note, .closing {
          font-size: 11px;
          line-height: 1.4;
          margin-top: 9mm;
          white-space: pre-wrap;
        }
        .payment-slip {
          margin-top: auto;
          border-top: 2px solid #111;
          display: grid;
          grid-template-columns: 39% 61%;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .payment-left,
        .payment-right {
          min-height: 96mm;
          padding-top: 6mm;
        }
        .payment-right {
          border-left: 2px solid #111;
          padding-left: 6mm;
        }
        .payment-title {
          font-size: 16px;
          font-weight: 700;
          margin-bottom: 5mm;
        }
        .payment-section {
          margin-bottom: 5mm;
          font-size: 11px;
          line-height: 1.35;
        }
        .payment-label {
          font-weight: 700;
          margin-bottom: 1.5mm;
        }
        .amount-row {
          display: flex;
          gap: 10mm;
          margin-top: 5mm;
        }
        .amount-col-label {
          font-size: 11px;
          margin-bottom: 1mm;
        }
        .amount-col-value {
          font-size: 16px;
        }
        .payment-right-grid {
          display: grid;
          grid-template-columns: 34mm 1fr;
          gap: 5mm;
          align-items: start;
        }
        .qr-code {
          width: 34mm;
          height: 34mm;
          display: block;
          object-fit: contain;
          margin-top: 1mm;
        }
        .payment-right-details {
          min-width: 0;
        }
        .payment-right .amount-row {
          margin-top: 4mm;
        }
        @media print {
          body {
            background: #fff;
          }
          .page {
            margin: 0;
            min-height: 292mm;
            box-shadow: none;
          }
        }
      </style>
    </head>
    <body>
      <div class="page">
        <img class="logo" src="${escapeHtml(logoSrc)}" alt="TimeTab Logo" />

        <div class="top">
          <div class="sender">${senderLines}</div>
          <div class="customer">${customerLines}</div>
        </div>

        <div class="date-line">Datum: ${escapeHtml(formatDate(invoice.invoiceDate))}</div>

        <h1>${escapeHtml(invoice.title)}</h1>
        <div class="timeframe">${escapeHtml(timeframeLabel)}</div>

        <table>
          <thead>
            <tr>
              <th>Bezeichnung</th>
              <th>Menge</th>
              <th>Preis pro Einheit</th>
              <th>Betrag</th>
            </tr>
          </thead>
          <tbody>
            ${itemRows}
            <tr class="total-row">
              <td>Total</td>
              <td></td>
              <td>CHF</td>
              <td>${escapeHtml(formatAmount(invoice.total))}</td>
            </tr>
          </tbody>
        </table>

        <div class="note">${escapeHtml(invoice.note)}</div>
        <div class="closing">${escapeHtml(invoice.closingLines.join("\n\n"))}</div>

        <div class="payment-slip">
          <div class="payment-left">
            <div class="payment-title">Empfangsschein</div>
            <div class="payment-section">
              <div class="payment-label">Konto/Zahlbar an</div>
              ${paymentLines}
            </div>
            <div class="payment-section">
              <div class="payment-label">Zahlbar durch</div>
              ${payableByLines}
            </div>
            <div class="amount-row">
              <div>
                <div class="amount-col-label">Wahrung</div>
                <div class="amount-col-value">CHF</div>
              </div>
              <div>
                <div class="amount-col-label">Betrag</div>
                <div class="amount-col-value">${escapeHtml(formatAmount(invoice.total))}</div>
              </div>
            </div>
          </div>
          <div class="payment-right">
            <div class="payment-title">Zahlteil</div>
            <div class="payment-right-grid">
              <img class="qr-code" src="${escapeHtml(qrCodeSrc)}" alt="Payment QR Code" />
              <div class="payment-right-details">
                <div class="payment-section">
                  <div class="payment-label">Konto/Zahlbar an</div>
                  ${paymentLines}
                </div>
                <div class="payment-section">
                  <div class="payment-label">Zahlbar durch</div>
                  ${payableByLines}
                </div>
                <div class="amount-row">
                  <div>
                    <div class="amount-col-label">Wahrung</div>
                    <div class="amount-col-value">CHF</div>
                  </div>
                  <div>
                    <div class="amount-col-label">Betrag</div>
                    <div class="amount-col-value">${escapeHtml(formatAmount(invoice.total))}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </body>
  </html>`;
};

const InvoiceReportsPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { success, error } = useToast();
  const token = localStorage.getItem("session_token");

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: `Bearer ${token}` },
    }),
    [token]
  );

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [customers, setCustomers] = useState([]);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerForm, setCustomerForm] = useState(emptyCustomerForm);
  const [isCustomerSaving, setIsCustomerSaving] = useState(false);
  const [forbiddenMessage, setForbiddenMessage] = useState("");

  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfDraft, setPdfDraft] = useState(createInvoiceDraft());
  const [generatedPreviewHtml, setGeneratedPreviewHtml] = useState("");
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const previewFrameRef = useRef(null);

  const expectedDatePreview = useMemo(
    () => addMonths(form.creation_date, form.subscription_months),
    [form.creation_date, form.subscription_months]
  );

  const resetPdfPreview = () => {
    setGeneratedPreviewHtml("");
  };

  const loadInvoices = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/innotab/invoices`, authHeaders);
      setRows(res.data?.invoices || []);
      setForbiddenMessage("");
    } catch (err) {
      const msg = err?.response?.data?.msg || t("invoice_report.errors.load_invoices");
      if (err?.response?.status === 403) {
        setForbiddenMessage(msg);
        setRows([]);
      } else {
        error(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/innotab/invoice-customers`, authHeaders);
      return res.data?.customers || [];
    } catch (err) {
      if (err?.response?.status !== 403) {
        error(err?.response?.data?.msg || t("invoice_report.errors.load_customers"));
      }
      return [];
    }
  };

  const loadCustomers = async () => {
    const nextCustomers = await fetchCustomers();
    setCustomers(nextCustomers);
    return nextCustomers;
  };

  useEffect(() => {
    loadInvoices();
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const findMatchingCustomer = (row = {}, sourceCustomers = customers) =>
    sourceCustomers.find(
      (customer) =>
        (customer.company_name || "").trim().toLowerCase() ===
          (row.customer_company_name || "").trim().toLowerCase() &&
        (customer.email || "").trim().toLowerCase() ===
          (row.customer_email || "").trim().toLowerCase()
    ) || null;

  const updateCustomerForm = (field, value) => {
    setCustomerForm((prev) => ({ ...prev, [field]: value }));
  };

  const applyCustomerToInvoiceForm = (customerIdRaw) => {
    const customerId = String(customerIdRaw || "");
    const customer = customers.find((entry) => String(entry.id) === customerId);
    updateForm("invoice_customer_id", customerId);
    if (!customer) return;

    setForm((prev) => ({
      ...prev,
      invoice_customer_id: customerId,
      business_unit: customer.subscription_name || prev.business_unit,
      customer_company_name: customer.company_name || "",
      customer_email: customer.email || "",
      invoice_value_net:
        customer.unit_cost !== null && customer.unit_cost !== undefined
          ? String(customer.unit_cost)
          : prev.invoice_value_net,
    }));
  };

  const applyCustomerToPdfDraft = (customerIdRaw) => {
    const customerId = String(customerIdRaw || "");
    const customer = customers.find((entry) => String(entry.id) === customerId);

    resetPdfPreview();
    setPdfDraft((prev) => {
      if (!customer) {
        return { ...prev, invoiceCustomerId: customerId };
      }

      const nextAddressLine1 = customer.care_of ? `c/o ${customer.care_of}` : customer.street || "";
      const nextAddressLine2 = customer.care_of ? customer.street || "" : buildCustomerCityLine(customer);
      const nextAddressLine3 = customer.care_of
        ? buildCustomerCountryLine(customer)
        : customer.country || "";

      return {
        ...prev,
        invoiceCustomerId: customerId,
        customerCompanyName: customer.company_name || prev.customerCompanyName,
        customerEmail: customer.email || prev.customerEmail,
        customerAddressLine1: nextAddressLine1,
        customerAddressLine2: nextAddressLine2,
        customerAddressLine3: nextAddressLine3,
        lineItems: (prev.lineItems || []).map((item, index) =>
          index === 0
            ? {
                ...item,
                description: customer.subscription_name
                  ? `${customer.subscription_name}`
                  : item.description,
                unit_price:
                  customer.unit_cost !== null && customer.unit_cost !== undefined
                    ? String(customer.unit_cost)
                    : item.unit_price,
              }
            : item
        ),
      };
    });
  };

  const openCreateDialog = () => {
    setEditingRow(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEditDialog = (row) => {
    const matchingCustomer = findMatchingCustomer(row);
    setEditingRow(row);
    setForm({
      invoice_customer_id: matchingCustomer ? String(matchingCustomer.id) : "",
      invoice_number: row.invoice_number || "",
      business_unit: row.business_unit || "",
      customer_company_name: row.customer_company_name || "",
      customer_email: row.customer_email || "",
      invoice_value_net: String(row.invoice_value_net ?? ""),
      invoice_tax: String(row.invoice_tax ?? ""),
      creation_date: row.creation_date || "",
      subscription_months: String(row.subscription_months ?? "1"),
      reminder_date: row.reminder_date || "",
      invoice_sent: Boolean(row.invoice_sent),
      invoice_paid: Boolean(row.invoice_paid),
    });
    setDialogOpen(true);
  };

  const closeDialog = (force = false) => {
    if (isSaving && !force) return;
    setDialogOpen(false);
    setEditingRow(null);
    setForm({ ...emptyForm });
  };

  const openCustomerDialog = () => {
    setEditingCustomer(null);
    setCustomerForm({ ...emptyCustomerForm });
    setCustomerDialogOpen(true);
  };

  const closeCustomerDialog = (force = false) => {
    if (isCustomerSaving && !force) return;
    setCustomerDialogOpen(false);
    setEditingCustomer(null);
    setCustomerForm({ ...emptyCustomerForm });
  };

  const startEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setCustomerForm({
      company_name: customer.company_name || "",
      care_of: customer.care_of || "",
      street: customer.street || "",
      first_name: customer.first_name || "",
      last_name: customer.last_name || "",
      email: customer.email || "",
      zip: customer.zip || "",
      city: customer.city || "",
      country: customer.country || "",
      subscription_name: customer.subscription_name || "",
      unit_cost: String(customer.unit_cost ?? ""),
    });
    setCustomerDialogOpen(true);
  };

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveInvoice = async () => {
    if (!form.invoice_number.trim()) return error(t("invoice_report.errors.invoice_number_required"));
    if (!form.customer_company_name.trim()) return error(t("invoice_report.errors.company_required"));
    if (!form.customer_email.trim()) return error(t("invoice_report.errors.email_required"));
    if (!form.creation_date) return error(t("invoice_report.errors.creation_date_required"));

    const subscriptionMonths = Number(form.subscription_months);
    if (!Number.isInteger(subscriptionMonths) || subscriptionMonths <= 0) {
      return error(t("invoice_report.errors.months_positive"));
    }

    const payload = {
      invoice_number: form.invoice_number.trim(),
      business_unit: form.business_unit.trim(),
      customer_company_name: form.customer_company_name.trim(),
      customer_email: form.customer_email.trim(),
      invoice_value_net: Number(form.invoice_value_net || 0),
      invoice_tax: Number(form.invoice_tax || 0),
      creation_date: form.creation_date,
      subscription_months: subscriptionMonths,
      reminder_date: form.reminder_date || null,
      invoice_sent: Boolean(form.invoice_sent),
      invoice_paid: Boolean(form.invoice_paid),
    };

    setIsSaving(true);
    try {
      if (editingRow) {
        await axios.put(`${API_BASE_URL}/api/innotab/invoices/${editingRow.id}`, payload, authHeaders);
        success(t("invoice_report.messages.invoice_updated"));
      } else {
        await axios.post(`${API_BASE_URL}/api/innotab/invoices`, payload, authHeaders);
        success(t("invoice_report.messages.invoice_created"));
      }
      closeDialog(true);
      await loadInvoices();
    } catch (err) {
      error(err?.response?.data?.msg || t("invoice_report.errors.save_invoice"));
    } finally {
      setIsSaving(false);
    }
  };

  const toggleFlag = async (row, field, checked) => {
    try {
      await axios.put(
        `${API_BASE_URL}/api/innotab/invoices/${row.id}`,
        { [field]: checked },
        authHeaders
      );
      setRows((prev) =>
        prev.map((item) => (item.id === row.id ? { ...item, [field]: checked } : item))
      );
      await loadInvoices();
    } catch (err) {
      error(err?.response?.data?.msg || t("invoice_report.errors.update_status"));
    }
  };

  const deleteInvoice = async () => {
    if (!deleteTarget) return;
    setIsSaving(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/innotab/invoices/${deleteTarget.id}`, authHeaders);
      success(t("invoice_report.messages.invoice_deleted"));
      setDeleteTarget(null);
      await loadInvoices();
    } catch (err) {
      error(err?.response?.data?.msg || t("invoice_report.errors.delete_invoice"));
    } finally {
      setIsSaving(false);
    }
  };

  const openPdfDialog = async (row) => {
    resetPdfPreview();
    const latestCustomers = await loadCustomers();
    setPdfDraft(createInvoiceDraft(row, findMatchingCustomer(row, latestCustomers)));
    setPdfDialogOpen(true);
  };

  const closePdfDialog = () => {
    resetPdfPreview();
    setPdfDialogOpen(false);
    setPdfDraft(createInvoiceDraft());
  };

  const updatePdfDraft = (field, value) => {
    resetPdfPreview();
    setPdfDraft((prev) => ({ ...prev, [field]: value }));
  };

  const saveCustomer = async () => {
    if (!customerForm.company_name.trim()) return error(t("invoice_report.errors.company_required"));
    if (!customerForm.email.trim()) return error(t("invoice_report.errors.email_required"));

    const unitCost = Number(customerForm.unit_cost || 0);
    if (!Number.isFinite(unitCost) || unitCost < 0) {
      return error(t("invoice_report.errors.unit_cost_positive"));
    }

    const payload = {
      company_name: customerForm.company_name.trim(),
      care_of: customerForm.care_of.trim(),
      street: customerForm.street.trim(),
      first_name: customerForm.first_name.trim(),
      last_name: customerForm.last_name.trim(),
      email: customerForm.email.trim(),
      zip: customerForm.zip.trim(),
      city: customerForm.city.trim(),
      country: customerForm.country.trim(),
      subscription_name: customerForm.subscription_name.trim(),
      unit_cost: unitCost,
    };

    setIsCustomerSaving(true);
    try {
      if (editingCustomer) {
        await axios.put(
          `${API_BASE_URL}/api/innotab/invoice-customers/${editingCustomer.id}`,
          payload,
          authHeaders
        );
        success(t("invoice_report.messages.customer_updated"));
      } else {
        await axios.post(`${API_BASE_URL}/api/innotab/invoice-customers`, payload, authHeaders);
        success(t("invoice_report.messages.customer_created"));
      }
      await loadCustomers();
      closeCustomerDialog(true);
    } catch (err) {
      error(err?.response?.data?.msg || t("invoice_report.errors.save_customer"));
    } finally {
      setIsCustomerSaving(false);
    }
  };

  const deleteCustomer = async (customer) => {
    if (!customer) return;

    setIsCustomerSaving(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/innotab/invoice-customers/${customer.id}`, authHeaders);
      setCustomers((prev) => prev.filter((entry) => entry.id !== customer.id));
      if (String(form.invoice_customer_id || "") === String(customer.id)) {
        updateForm("invoice_customer_id", "");
      }
      if (String(pdfDraft.invoiceCustomerId || "") === String(customer.id)) {
        setPdfDraft((prev) => ({ ...prev, invoiceCustomerId: "" }));
      }
      if (editingCustomer?.id === customer.id) {
        setEditingCustomer(null);
        setCustomerForm({ ...emptyCustomerForm });
      }
      success(t("invoice_report.messages.customer_deleted"));
    } catch (err) {
      error(err?.response?.data?.msg || t("invoice_report.errors.delete_customer"));
    } finally {
      setIsCustomerSaving(false);
    }
  };

  const updatePdfLineItem = (index, field, value) => {
    resetPdfPreview();
    setPdfDraft((prev) => ({
      ...prev,
      lineItems: prev.lineItems.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const addPdfLineItem = () => {
    resetPdfPreview();
    setPdfDraft((prev) => ({
      ...prev,
      lineItems: [...prev.lineItems, createEmptyInvoiceItem()],
    }));
  };

  const removePdfLineItem = (index) => {
    resetPdfPreview();
    setPdfDraft((prev) => ({
      ...prev,
      lineItems:
        prev.lineItems.length <= 1
          ? [createEmptyInvoiceItem()]
          : prev.lineItems.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const validatePdfDraft = () => {
    if (!pdfDraft.customerCompanyName.trim()) {
      error(t("invoice_report.errors.company_required"));
      return false;
    }
    if (!pdfDraft.invoiceDate) {
      error(t("invoice_report.errors.invoice_date_required"));
      return false;
    }
    const hasValidItem = (pdfDraft.lineItems || []).some(
      (item) => item.description.trim() && Number(item.quantity || 0) > 0
    );
    if (!hasValidItem) {
      error(t("invoice_report.errors.line_required"));
      return false;
    }
    return true;
  };

  const generatePdfPreview = async () => {
    if (!validatePdfDraft()) return null;

    setIsGeneratingPreview(true);
    try {
      const nextHtml = await buildInvoiceHtml(pdfDraft);
      setGeneratedPreviewHtml(nextHtml);
      success(t("invoice_report.messages.preview_generated"));
      return nextHtml;
    } catch (err) {
      console.error("Invoice document generation failed", err);
      error(err?.message || t("invoice_report.errors.generate_preview"));
      return null;
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const downloadPdf = async () => {
    let html = generatedPreviewHtml;
    if (!html) {
      html = await generatePdfPreview();
    }
    if (!html) return;

    const frameWindow = previewFrameRef.current?.contentWindow;
    if (!frameWindow) {
      error(t("invoice_report.errors.preview_not_ready"));
      return;
    }

    frameWindow.focus();
    frameWindow.print();
  };

  const invoicePreviewTotals = useMemo(() => {
    const items = (pdfDraft.lineItems || []).map((item) => ({
      ...item,
      quantityNumber: Number(item.quantity || 0),
      unitPriceNumber: Number(item.unit_price || 0),
    }));
    const total = items.reduce(
      (sum, item) => sum + (Number.isFinite(item.quantityNumber) ? item.quantityNumber : 0) * (Number.isFinite(item.unitPriceNumber) ? item.unitPriceNumber : 0),
      0
    );
    return { items, total };
  }, [pdfDraft.lineItems]);

  const columns = [
    { field: "invoice_number", headerName: t("invoice_report.columns.number"), flex: 0.2, minWidth: 60 },
    { field: "business_unit", headerName: "BU", flex: 0.8, minWidth: 110 },
    { field: "customer_company_name", headerName: t("invoice_report.columns.company"), flex: 1, minWidth: 120 },
    { field: "customer_email", headerName: t("invoice_report.columns.customer_email"), flex: 1.4, minWidth: 220 },
    {
      field: "invoice_value_net",
      headerName: t("invoice_report.columns.net"),
      minWidth: 80,
      valueFormatter: ({ value }) => formatAmount(value),
    },
    {
      field: "invoice_tax",
      headerName: t("invoice_report.columns.tax"),
      minWidth: 60,
      valueFormatter: ({ value }) => formatAmount(value),
    },
    {
      field: "subscription_months",
      headerName: t("invoice_report.columns.duration"),
      width: 80,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "creation_date",
      headerName: t("invoice_report.columns.creation_date"),
      minWidth: 80,
      valueFormatter: ({ value }) => formatDate(value),
    },
    {
      field: "expected_next_invoice_date",
      headerName: t("invoice_report.columns.next_invoice"),
      minWidth: 80,
      valueFormatter: ({ value }) => formatDate(value),
    },
    {
      field: "invoice_sent",
      headerName: t("invoice_report.columns.sent"),
      minWidth: 10,
      sortable: false,
      renderCell: (params) => (
        <Checkbox
          checked={Boolean(params.row.invoice_sent)}
          onChange={(event) => {
            event.stopPropagation();
            toggleFlag(params.row, "invoice_sent", event.target.checked);
          }}
        />
      ),
    },
    {
      field: "invoice_paid",
      headerName: t("invoice_report.columns.paid"),
      minWidth: 10,
      sortable: false,
      renderCell: (params) => (
        <Checkbox
          checked={Boolean(params.row.invoice_paid)}
          onChange={(event) => {
            event.stopPropagation();
            toggleFlag(params.row, "invoice_paid", event.target.checked);
          }}
        />
      ),
    },
    {
      field: "reminder_date",
      headerName: t("invoice_report.columns.reminder_date"),
      minWidth: 80,
      valueFormatter: ({ value }) => formatDate(value),
    },
    {
      field: "actions",
      headerName: "",
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            color={params.row.overdue_reminder ? "warning" : "default"}
            onClick={(event) => {
              event.stopPropagation();
              openPdfDialog(params.row);
            }}
            title={t("invoice_report.create_pdf")}
          >
            <PictureAsPdfIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              openEditDialog(params.row);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={(event) => {
              event.stopPropagation();
              setDeleteTarget(params.row);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  if (isLoading) return <Loading />;

  return (
    <Box m="20px">
      <TitleHeader
        title={t("invoice_report.title")}
        subtitle={t("invoice_report.subtitle")}
        actions={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button variant="outlined" onClick={openCustomerDialog}>
              {t("invoice_report.manage_customers")}
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
              {t("invoice_report.new_invoice")}
            </Button>
          </Stack>
        }
      />

      {forbiddenMessage ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1">{forbiddenMessage}</Typography>
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            height: "72vh",
            border: `1px solid ${theme.palette.divider}`,
            "& .MuiDataGrid-row:hover .MuiDataGrid-cell": {
              backgroundColor: "action.hover",
            },
            "& .invoice-row--red .MuiDataGrid-cell": {
              backgroundColor: theme.palette.error.light,
            },
            "& .invoice-row--red:hover .MuiDataGrid-cell": {
              backgroundColor: theme.palette.error.light,
            },
            "& .invoice-row--orange .MuiDataGrid-cell": {
              backgroundColor: theme.palette.warning.light,
            },
            "& .invoice-row--orange:hover .MuiDataGrid-cell": {
              backgroundColor: theme.palette.warning.light,
            },
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25, page: 0 } },
            }}
            getRowClassName={(params) => {
              if (params.row.status_color === "red") return "invoice-row--red";
              if (params.row.status_color === "orange") return "invoice-row--orange";
              return "";
            }}
          />
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingRow ? t("invoice_report.edit_invoice") : t("invoice_report.new_invoice")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
              <TextField
                select
                label={t("invoice_report.saved_customer")}
                value={form.invoice_customer_id}
                onChange={(event) => applyCustomerToInvoiceForm(event.target.value)}
                fullWidth
              >
                <MenuItem value="">{t("none")}</MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={String(customer.id)}>
                    {customer.company_name}
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="outlined" onClick={openCustomerDialog} sx={{ minWidth: 180 }}>
                {t("invoice_report.manage_customers")}
              </Button>
            </Stack>
            <TextField
              label={t("invoice_report.invoice_number")}
              value={form.invoice_number}
              onChange={(event) => updateForm("invoice_number", event.target.value)}
              fullWidth
            />
            <TextField
              label="BU"
              value={form.business_unit}
              onChange={(event) => updateForm("business_unit", event.target.value)}
              fullWidth
            />
            <TextField
              label={t("invoice_report.customer_company_name")}
              value={form.customer_company_name}
              onChange={(event) => updateForm("customer_company_name", event.target.value)}
              fullWidth
            />
            <TextField
              label={t("invoice_report.customer_email")}
              type="email"
              value={form.customer_email}
              onChange={(event) => updateForm("customer_email", event.target.value)}
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label={t("invoice_report.invoice_value_net")}
                type="number"
                value={form.invoice_value_net}
                onChange={(event) => updateForm("invoice_value_net", event.target.value)}
                fullWidth
              />
              <TextField
                label={t("invoice_report.invoice_tax")}
                type="number"
                value={form.invoice_tax}
                onChange={(event) => updateForm("invoice_tax", event.target.value)}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label={t("invoice_report.creation_date")}
                type="date"
                value={form.creation_date}
                onChange={(event) => updateForm("creation_date", event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label={t("invoice_report.subscription_months")}
                type="number"
                value={form.subscription_months}
                onChange={(event) => updateForm("subscription_months", event.target.value)}
                fullWidth
              />
            </Stack>
            <TextField
              label={t("invoice_report.reminder_date")}
              type="date"
              value={form.reminder_date}
              onChange={(event) => updateForm("reminder_date", event.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={
                expectedDatePreview
                  ? t("invoice_report.expected_next_invoice", { date: formatDate(expectedDatePreview) })
                  : t("invoice_report.expected_next_invoice_help")
              }
              fullWidth
            />
            <Stack direction="row" spacing={3}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox
                  checked={Boolean(form.invoice_sent)}
                  onChange={(event) => updateForm("invoice_sent", event.target.checked)}
                />
                <Typography>{t("invoice_report.invoice_sent")}</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox
                  checked={Boolean(form.invoice_paid)}
                  onChange={(event) => updateForm("invoice_paid", event.target.checked)}
                />
                <Typography>{t("invoice_report.invoice_paid")}</Typography>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={isSaving}>
            {t("cancel")}
          </Button>
          <Button onClick={saveInvoice} variant="contained" disabled={isSaving}>
            {t("invoice_report.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("invoice_report.delete_invoice")}</DialogTitle>
        <DialogContent>
          <Typography>{t("invoice_report.delete_invoice_question", { number: deleteTarget?.invoice_number || "" })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={isSaving}>
            {t("cancel")}
          </Button>
          <Button color="error" variant="contained" onClick={deleteInvoice} disabled={isSaving}>
            {t("invoice_report.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pdfDialogOpen} onClose={closePdfDialog} fullWidth maxWidth="xl">
        <DialogTitle>{t("invoice_report.create_pdf")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary">
              {t("invoice_report.pdf_help")}
            </Typography>

            <Stack direction={{ xs: "column", lg: "row" }} spacing={3} alignItems="stretch">
              <Stack spacing={2} sx={{ flex: 1.05, minWidth: 0 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                  <TextField
                    select
                    label={t("invoice_report.saved_customer")}
                    value={pdfDraft.invoiceCustomerId || ""}
                    onChange={(event) => applyCustomerToPdfDraft(event.target.value)}
                    fullWidth
                  >
                    <MenuItem value="">{t("none")}</MenuItem>
                    {customers.map((customer) => (
                      <MenuItem key={customer.id} value={String(customer.id)}>
                        {customer.company_name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button variant="outlined" onClick={openCustomerDialog} sx={{ minWidth: 180 }}>
                    {t("invoice_report.manage_customers")}
                  </Button>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label={t("invoice_report.invoice_number")}
                    value={pdfDraft.invoiceNumber}
                    onChange={(event) => updatePdfDraft("invoiceNumber", event.target.value)}
                    fullWidth
                  />
                  <TextField
                    label={t("invoice_report.invoice_date")}
                    type="date"
                    value={pdfDraft.invoiceDate}
                    onChange={(event) => updatePdfDraft("invoiceDate", event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Stack>

                <TextField
                  label={t("invoice_report.customer_company")}
                  value={pdfDraft.customerCompanyName}
                  onChange={(event) => updatePdfDraft("customerCompanyName", event.target.value)}
                  fullWidth
                />
                <TextField
                  label={t("invoice_report.customer_email")}
                  value={pdfDraft.customerEmail}
                  onChange={(event) => updatePdfDraft("customerEmail", event.target.value)}
                  fullWidth
                />
                <TextField
                  label={t("invoice_report.address_line", { number: 1 })}
                  value={pdfDraft.customerAddressLine1}
                  onChange={(event) => updatePdfDraft("customerAddressLine1", event.target.value)}
                  placeholder={t("invoice_report.street_and_number")}
                  fullWidth
                />
                <TextField
                  label={t("invoice_report.address_line", { number: 2 })}
                  value={pdfDraft.customerAddressLine2}
                  onChange={(event) => updatePdfDraft("customerAddressLine2", event.target.value)}
                  placeholder={t("invoice_report.zip_and_city")}
                  fullWidth
                />
                <TextField
                  label={t("invoice_report.address_line", { number: 3 })}
                  value={pdfDraft.customerAddressLine3}
                  onChange={(event) => updatePdfDraft("customerAddressLine3", event.target.value)}
                  placeholder={t("invoice_report.country_or_extra")}
                  fullWidth
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label={t("invoice_report.document_title")}
                    value={pdfDraft.title}
                    onChange={(event) => updatePdfDraft("title", event.target.value)}
                    fullWidth
                  />
                  <TextField
                    label={t("invoice_report.timeframe_label")}
                    value={pdfDraft.introLabel}
                    onChange={(event) => updatePdfDraft("introLabel", event.target.value)}
                    fullWidth
                  />
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label={t("invoice_report.timeframe_start")}
                    type="date"
                    value={pdfDraft.timeframeStart}
                    onChange={(event) => updatePdfDraft("timeframeStart", event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label={t("invoice_report.timeframe_end")}
                    type="date"
                    value={pdfDraft.timeframeEnd}
                    onChange={(event) => updatePdfDraft("timeframeEnd", event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Stack>

                <TextField
                  label={t("invoice_report.payment_note")}
                  value={pdfDraft.note}
                  onChange={(event) => updatePdfDraft("note", event.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label={t("invoice_report.closing_line", { number: 1 })}
                    value={pdfDraft.closingLine1}
                    onChange={(event) => updatePdfDraft("closingLine1", event.target.value)}
                    fullWidth
                  />
                  <TextField
                    label={t("invoice_report.closing_line", { number: 2 })}
                    value={pdfDraft.closingLine2}
                    onChange={(event) => updatePdfDraft("closingLine2", event.target.value)}
                    fullWidth
                  />
                </Stack>

                <Divider />

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">{t("invoice_report.invoice_lines")}</Typography>
                  <Button startIcon={<AddIcon />} onClick={addPdfLineItem}>
                    {t("invoice_report.add_line")}
                  </Button>
                </Stack>

                {(pdfDraft.lineItems || []).map((item, index) => (
                  <Paper
                    key={`invoice-item-${index}`}
                    variant="outlined"
                    sx={{ p: 2, borderColor: theme.palette.divider }}
                  >
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2">{t("invoice_report.line_number", { number: index + 1 })}</Typography>
                        <IconButton size="small" color="error" onClick={() => removePdfLineItem(index)}>
                          <RemoveCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <TextField
                        label={t("invoice_report.description")}
                        value={item.description}
                        onChange={(event) => updatePdfLineItem(index, "description", event.target.value)}
                        fullWidth
                      />
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <TextField
                          label={t("invoice_report.quantity")}
                          type="number"
                          value={item.quantity}
                          onChange={(event) => updatePdfLineItem(index, "quantity", event.target.value)}
                          fullWidth
                        />
                        <TextField
                          label={t("invoice_report.unit_price")}
                          type="number"
                          value={item.unit_price}
                          onChange={(event) => updatePdfLineItem(index, "unit_price", event.target.value)}
                          fullWidth
                        />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {t("invoice_report.line_total")}:{" "}
                        {formatCurrency(
                          Number(item.quantity || 0) * Number(item.unit_price || 0)
                        )}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>

              <Stack spacing={2} sx={{ flex: 0.95, minWidth: 0 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="h6">{t("invoice_report.quick_summary")}</Typography>
                    <Typography variant="body2">
                      {t("invoice_report.customer")}: {pdfDraft.customerCompanyName || "-"}
                    </Typography>
                    <Typography variant="body2">
                      {t("invoice_report.timeframe")}:{" "}
                      {pdfDraft.timeframeStart || pdfDraft.timeframeEnd
                        ? `${formatDate(pdfDraft.timeframeStart)} - ${formatDate(pdfDraft.timeframeEnd)}`
                        : "-"}
                    </Typography>
                    <Typography variant="body2">
                      {t("invoice_report.total")}: {formatCurrency(invoicePreviewTotals.total)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("invoice_report.preview_help")}
                    </Typography>
                  </Stack>
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    flex: 1,
                    minHeight: 520,
                    overflow: "hidden",
                    bgcolor: "#fafafa",
                  }}
                >
                  {generatedPreviewHtml ? (
                    <iframe
                      ref={previewFrameRef}
                      title={t("invoice_report.preview_title")}
                      srcDoc={generatedPreviewHtml}
                      style={{ width: "100%", height: "100%", minHeight: 520, border: 0 }}
                    />
                  ) : (
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      spacing={1}
                      sx={{ minHeight: 520, px: 3, textAlign: "center" }}
                    >
                      <PictureAsPdfIcon sx={{ fontSize: 42, color: "text.secondary" }} />
                      <Typography variant="h6">{t("invoice_report.no_preview")}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("invoice_report.no_preview_help")}
                      </Typography>
                    </Stack>
                  )}
                </Paper>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePdfDialog}>{t("invoice_report.close")}</Button>
          <Button
            onClick={generatePdfPreview}
            startIcon={<PreviewIcon />}
            disabled={isGeneratingPreview}
          >
            {t("invoice_report.preview_document")}
          </Button>
          <Button
            onClick={downloadPdf}
            startIcon={<DownloadIcon />}
            variant="contained"
            disabled={isGeneratingPreview}
          >
            {t("invoice_report.save_as_pdf")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={customerDialogOpen} onClose={closeCustomerDialog} fullWidth maxWidth="lg">
        <DialogTitle>{editingCustomer ? t("invoice_report.edit_customer") : t("invoice_report.manage_customers")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Paper variant="outlined" sx={{ maxHeight: 260, overflow: "auto" }}>
              <Stack divider={<Divider flexItem />}>
                {customers.length ? (
                  customers.map((customer) => (
                    <Stack
                      key={customer.id}
                      direction={{ xs: "column", md: "row" }}
                      justifyContent="space-between"
                      spacing={2}
                      sx={{ p: 2 }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1">{customer.company_name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {[customer.first_name, customer.last_name].filter(Boolean).join(" ")}
                          {[customer.first_name || customer.last_name ? " | " : "", customer.email || ""].join("")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {[customer.subscription_name, formatCurrency(customer.unit_cost)]
                            .filter(Boolean)
                            .join(" | ")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {[customer.street, buildCustomerCountryLine(customer)].filter(Boolean).join(" | ")}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Button variant="outlined" onClick={() => startEditCustomer(customer)}>
                          {t("invoice_report.edit")}
                        </Button>
                        <Button
                          color="error"
                          variant="outlined"
                          onClick={() => deleteCustomer(customer)}
                          disabled={isCustomerSaving}
                        >
                          {t("invoice_report.delete")}
                        </Button>
                      </Stack>
                    </Stack>
                  ))
                ) : (
                  <Typography sx={{ p: 2 }} color="text.secondary">
                    {t("invoice_report.no_customers")}
                  </Typography>
                )}
              </Stack>
            </Paper>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                {editingCustomer ? t("invoice_report.edit_selected_customer") : t("invoice_report.create_customer")}
              </Typography>
              {editingCustomer ? (
                <Button
                  onClick={() => {
                    setEditingCustomer(null);
                    setCustomerForm({ ...emptyCustomerForm });
                  }}
                >
                  {t("invoice_report.new_customer")}
                </Button>
              ) : null}
            </Stack>

            <Stack spacing={2}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label={t("invoice_report.company_name")}
                  value={customerForm.company_name}
                  onChange={(event) => updateCustomerForm("company_name", event.target.value)}
                  fullWidth
                />
                <TextField
                  label="c/o"
                  value={customerForm.care_of}
                  onChange={(event) => updateCustomerForm("care_of", event.target.value)}
                  fullWidth
                />
              </Stack>

              <TextField
                label={t("invoice_report.street")}
                value={customerForm.street}
                onChange={(event) => updateCustomerForm("street", event.target.value)}
                fullWidth
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label={t("invoice_report.first_name")}
                  value={customerForm.first_name}
                  onChange={(event) => updateCustomerForm("first_name", event.target.value)}
                  fullWidth
                />
                <TextField
                  label={t("invoice_report.last_name")}
                  value={customerForm.last_name}
                  onChange={(event) => updateCustomerForm("last_name", event.target.value)}
                  fullWidth
                />
              </Stack>

              <TextField
                label="Email"
                type="email"
                value={customerForm.email}
                onChange={(event) => updateCustomerForm("email", event.target.value)}
                fullWidth
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label={t("invoice_report.zip")}
                  value={customerForm.zip}
                  onChange={(event) => updateCustomerForm("zip", event.target.value)}
                  fullWidth
                />
                <TextField
                  label={t("invoice_report.city")}
                  value={customerForm.city}
                  onChange={(event) => updateCustomerForm("city", event.target.value)}
                  fullWidth
                />
                <TextField
                  label={t("invoice_report.country")}
                  value={customerForm.country}
                  onChange={(event) => updateCustomerForm("country", event.target.value)}
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label={t("invoice_report.subscription_name")}
                  value={customerForm.subscription_name}
                  onChange={(event) => updateCustomerForm("subscription_name", event.target.value)}
                  fullWidth
                />
                <TextField
                  label={t("invoice_report.unit_cost")}
                  type="number"
                  value={customerForm.unit_cost}
                  onChange={(event) => updateCustomerForm("unit_cost", event.target.value)}
                  fullWidth
                />
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCustomerDialog} disabled={isCustomerSaving}>
            {t("invoice_report.close")}
          </Button>
          <Button onClick={saveCustomer} variant="contained" disabled={isCustomerSaving}>
            {t("invoice_report.save_customer")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceReportsPage;
